/**
 * Sparkline — mini-gráfico SVG inline para mostrar tendencia 7d (paquete AE).
 *
 * Renderiza una polilínea simple con un punto resaltado al final. Sin ejes,
 * sin labels — diseñado para ir debajo de un KPI numérico grande dando
 * contexto temporal de un vistazo (¿está subiendo? ¿bajando? ¿plano?).
 *
 * Convenciones del brandbook:
 *   - Stroke 1.5px con color del KPI.
 *   - Width 60px / Height 18px (cabe perfecto debajo del valor numérico).
 *   - Sin animación de "draw" — el `stagger` del Dashboard ya da la
 *     sensación de "aparición" — no apilar animaciones.
 *
 * Si todos los valores son 0, mostramos una línea plana abajo (no se "estira"
 * artificialmente con un max de 1). Si el dataset es uniforme (todos iguales)
 * trazamos línea horizontal centrada.
 */

interface SparklineProps {
  /** Array de números (típicamente 7 — días). */
  values: readonly number[];
  /** Color del trazo. Default: azul institucional. */
  color?: string;
  /** Ancho en px. */
  width?: number;
  /** Alto en px. */
  height?: number;
  /** Tooltip accesible — incluir el rango de la métrica. */
  ariaLabel?: string;
}

export function Sparkline({
  values,
  color = '#003057',
  width = 60,
  height = 18,
  ariaLabel,
}: SparklineProps) {
  if (values.length < 2) {
    return null;
  }
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1; // evitar /0 — datos uniformes → línea plana
  const stepX = width / (values.length - 1);

  // Y de cada punto. Si todos iguales, queda en el medio del canvas.
  const yOf = (v: number): number => {
    if (max === min) return height / 2;
    return height - ((v - min) / range) * height;
  };

  const points = values.map((v, i) => ({ x: i * stepX, y: yOf(v) }));
  const last = points[points.length - 1];

  const pathD = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ');

  // Path para el área (fill suave debajo de la línea, opacity baja)
  const areaD =
    pathD +
    ` L ${(width).toFixed(2)} ${height} L 0 ${height} Z`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      role="img"
      aria-label={ariaLabel ?? `Tendencia: ${values.join(', ')}`}
      className="overflow-visible"
    >
      <path d={areaD} fill={color} fillOpacity={0.08} />
      <path
        d={pathD}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {last && <circle cx={last.x} cy={last.y} r={1.8} fill={color} />}
    </svg>
  );
}
